import { Form, Formik } from 'formik';
import { useIsUsCurrentTalent } from 'hooks/talents/useCurrentTalentRegion';
import Lottie from 'lottie-react';
import React from 'react';
import i18n from 'i18n';
import { useTranslation } from 'react-i18next';

import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import { Grid } from '@mui/material';

import {
  MutationCreateJobSuggestionArgs,
  Job,
  JobTypeEnum,
} from '@libs/graphql-types';
import { isNumber } from '@libs/helpers/common';
import { formatCurrency } from '@libs/helpers/format';
import Button from '@libs/ui/components/button';
import { ConnectedTextField } from '@libs/ui/components/form/text-field';
import { DefaultModal } from '@libs/ui/components/modals';
import Typography, { OuterLink } from '@libs/ui/components/typography';

import getLottieData from './assets/lottie-earn';
import validator from './validator';

interface InitialInviteModalProps {
  onSubmit: (vars: Omit<MutationCreateJobSuggestionArgs, 'job_id'>) => void;
  open: boolean;
  handleClose: VoidFunction;
  loading: boolean;
  job: Job;
}

const getData = ({ fee, isUs }: { fee?: string | number; isUs: boolean }) => ({
  [JobTypeEnum.Freelance]: {
    title: i18n.t('inviteModal.earnReferral', { ns: 'jobBoard' }),
    subtitle: (
      <>
        {i18n.t('inviteModal.checkDetails', { ns: 'jobBoard' })}{' '}
        <OuterLink
          href="https://www.notion.so/opentalent/Get-Paid-830a2d2bd1c34f51a06a0c2637564c18?pvs=4"
          target="_blank"
        >
          {i18n.t('inviteModal.here', { ns: 'jobBoard' })}
        </OuterLink>
      </>
    ),
  },
  [JobTypeEnum.Permanent]: {
    title: isNumber(fee)
      ? i18n.t('inviteModal.earnWithFee', { ns: 'jobBoard', fee: formatCurrency(fee) })
      : i18n.t('inviteModal.earnDefault', { ns: 'jobBoard' }),
    subtitle: i18n.t('inviteModal.findersFeeSubtitle', { ns: 'jobBoard' }),
  },
  [JobTypeEnum.Project]: {
    title: isNumber(fee)
      ? i18n.t('inviteModal.earnWithFee', { ns: 'jobBoard', fee: formatCurrency(fee) })
      : i18n.t('inviteModal.earnReferral', { ns: 'jobBoard' }),
    subtitle: i18n.t('inviteModal.findersFeeSubtitle', { ns: 'jobBoard' }),
  },
});

const InitialInviteModal = ({
  onSubmit,
  open,
  handleClose,
  loading,
  job,
}: InitialInviteModalProps) => {
  const { t } = useTranslation('jobBoard');
  const isUsTalent = useIsUsCurrentTalent();
  const fee = job?.finders_fee;
  const data = getData({
    fee,
    isUs: isUsTalent,
  })[job.type ? job.type : JobTypeEnum.Freelance];

  return (
    <>
      <Formik
        validationSchema={validator}
        onSubmit={onSubmit}
        initialValues={{ first_name: '', email: '' }}
      >
        {({ handleSubmit }) => (
          <Form>
            <DefaultModal
              title={t('inviteModal.title')}
              handleClose={handleClose}
              open={open}
              actions={
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  autoFocus
                  size="large"
                  disabled={loading}
                  onClick={handleSubmit}
                >
                  {t('inviteModal.sendInvite')}
                </Button>
              }
            >
              <br />
              <div style={{ width: 400, margin: '0 auto' }}>
                <Lottie loop animationData={JSON.parse(getLottieData(`€`))} />
              </div>
              <Typography
                style={{ fontWeight: 500, marginBottom: 8 }}
                variant="body1"
              >
                {isUsTalent ? t('inviteModal.referFriend') : data.title}
              </Typography>

              {!isUsTalent && (
                <Typography
                  style={{ width: 330, margin: '8px auto' }}
                  variant="body2"
                  color="textSecondary"
                >
                  {data.subtitle}
                </Typography>
              )}

              <br />
              <Grid spacing={4} direction="column" container>
                <Grid item>
                  <ConnectedTextField
                    name="name"
                    fullWidth
                    variant="outlined"
                    placeholder={t('inviteModal.firstName')}
                    InputProps={{
                      startAdornment: <PermIdentityIcon color="disabled" />,
                    }}
                  />
                </Grid>
                <Grid item>
                  <ConnectedTextField
                    name="email"
                    type="email"
                    fullWidth
                    variant="outlined"
                    placeholder={t('inviteModal.email')}
                    InputProps={{
                      startAdornment: <MailOutlineIcon color="disabled" />,
                    }}
                  />
                </Grid>
              </Grid>
            </DefaultModal>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default InitialInviteModal;
